import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { AlertPerson } from "../data/provider";
import { useSession } from "./session";

/** How often the open-alarm badge is refreshed; alarms are not in the Realtime publication, so this is polling (like the Devices screen). Screens reload on every refresh. */
export const ALARM_POLL_MS = 20_000;

type AlarmCountValue = {
  /** Open alarms the user may see; null until the first load. */
  count: number | null;
  /** Bumped on every successful refresh, so screens can reload their lists. */
  version: number;
  refresh(): Promise<void>;
};

const AlarmCountContext = createContext<AlarmCountValue>({ count: null, version: 0, refresh: async () => undefined });

/** Keeps the open-alarm count of the selected tenant (RLS limits it to the user's sites). */
export function AlarmCountProvider({ children, pollMs = ALARM_POLL_MS }: { children: ReactNode; pollMs?: number }) {
  const { provider, tenant } = useSession();
  const tenantId = tenant?.id ?? null;
  const [count, setCount] = useState<number | null>(null);
  const [version, setVersion] = useState(0);
  const current = useRef(tenantId);
  current.current = tenantId;

  const refresh = useCallback(async () => {
    if (!tenantId) return;
    try {
      const n = await provider.countOpenAlarms(tenantId);
      if (current.current !== tenantId) return;
      setCount(n);
      setVersion((v) => v + 1);
    } catch {
      // The badge is a convenience; keep the last value.
    }
  }, [provider, tenantId]);

  useEffect(() => {
    setCount(null);
    if (!tenantId) return;
    void refresh();
    if (pollMs <= 0) return;
    const h = setInterval(() => void refresh(), pollMs);
    return () => clearInterval(h);
  }, [tenantId, refresh, pollMs]);

  const value = useMemo(() => ({ count, version, refresh }), [count, version, refresh]);
  return <AlarmCountContext.Provider value={value}>{children}</AlarmCountContext.Provider>;
}

export function useAlarmCount(): AlarmCountValue {
  return useContext(AlarmCountContext);
}

/** Console users visible to the signed-in user (names of recipients, night contacts, acknowledgers). */
export function useAlertPeople(): { people: AlertPerson[]; loaded: boolean } {
  const { provider, tenant } = useSession();
  const tenantId = tenant?.id ?? null;
  const [state, setState] = useState<{ people: AlertPerson[]; loaded: boolean }>({ people: [], loaded: false });
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    provider
      .listAlertPeople(tenantId)
      .then((people) => !cancelled && setState({ people, loaded: true }))
      .catch(() => !cancelled && setState({ people: [], loaded: true }));
    return () => {
      cancelled = true;
    };
  }, [provider, tenantId]);
  return state;
}
